import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { Circles } from 'react-loader-spinner'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { EMPTY_OBJECT } from '../../constants/globalConstants'
import { getStripeForFacility, STRIPE_CAPTURE, stripePaymentElementsOptions, stripeSetupElementsOptions } from './stripeClient'
import { paymentElementOptions } from './stripeAppearance'
import { getStripeErrorMessage } from './stripeErrors'
import { stripePaymentToken } from './stripePayload'
import StripeFormSkeleton from './StripeFormSkeleton'

// Stripe fields are loaded from js.stripe.com; if they are not ready by then, show the form with an error
const STRIPE_READY_TIMEOUT_MS = 20000
const LOAD_ERROR_MESSAGE = 'The card form could not be loaded. Please refresh the page and try again.'

const DefaultComponent = () => <></>

// Stripe Payment Element (deferred intent). Like the Datacap / Heartland iframes it only tokenizes in the
// browser and hands the token to the screen the same way (paymentToken { Token: ctoken_..., ... }); the
// backend creates / confirms the PaymentIntent (or the SetupIntent on Add Card) on the existing endpoint.
function StripeCardForm(props) {
    const { isSubmittingForm, page, consent, previousButton } = props
    const stripe = useStripe()
    const elements = useElements()
    const [isTokenizing, setIsTokenizing] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    // 'loading' until the Payment Element is ready, then 'ready' (or 'error' if it failed to load)
    const [status, setStatus] = useState('loading')
    const statusRef = useRef('loading')
    // The props of the latest render (form data, submit) when the token arrives
    const latestProps = useRef(props)
    latestProps.current = props

    const updateStatus = (value) => {
        statusRef.current = value
        setStatus(value)
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            if (statusRef.current === 'loading') {
                setErrorMsg(LOAD_ERROR_MESSAGE)
                updateStatus('error')
            }
        }, STRIPE_READY_TIMEOUT_MS)
        return () => clearTimeout(timer)
    }, [])

    // The token goes to the screen the same way as the Datacap iframe token
    const handToScreen = (paymentToken) => {
        const { onSubmit, setValue, setFormData, formData, APILoad } = latestProps.current
        if (APILoad) APILoad.current = false
        if (page == 'reservation') {
            setValue('paymentToken', paymentToken)
            onSubmit()
        } else if (page == 'addCard') {
            onSubmit(paymentToken)
        } else {
            setFormData({ ...formData, 'paymentToken': paymentToken })
            onSubmit({ ...formData, 'paymentToken': paymentToken })
        }
    }

    const handlePay = async () => {
        if (!stripe || !elements || isTokenizing) return
        setErrorMsg('')
        setIsTokenizing(true)
        const { error: submitError } = await elements.submit()
        if (submitError) {
            setIsTokenizing(false)
            // Field errors (empty / invalid card) are already shown by Stripe under each field
            if (submitError.type !== 'validation_error') setErrorMsg(getStripeErrorMessage(submitError))
            if (latestProps.current.onInvalid) latestProps.current.onInvalid()
            return
        }
        const { error, confirmationToken } = await stripe.createConfirmationToken({ elements })
        setIsTokenizing(false)
        if (error) {
            setErrorMsg(getStripeErrorMessage(error))
            return
        }
        handToScreen(stripePaymentToken(confirmationToken.id))
    }

    const isLoading = isTokenizing || isSubmittingForm
    const isLoadingForm = status === 'loading'

    return (
        <div className='card shadow rounded-lg'>
            <div className="card-header">
                <h3 className="card-title">{page == 'addCard' ? 'Card Details' : 'Payment Details'}</h3>
            </div>
            <div className="card-body" style={{ position: 'relative' }}>
                {isLoadingForm && <StripeFormSkeleton />}
                {/* Kept mounted (Stripe must load) but invisible until its fields are ready */}
                <div
                    aria-hidden={isLoadingForm}
                    style={isLoadingForm ? { position: 'absolute', top: 0, left: 0, right: 0, opacity: 0, pointerEvents: 'none' } : undefined}>
                    {/* Stripe's own labels cannot show a required sign */}
                    <div className='small mb-2'>Card number, expiration date and security code are required <span className='text-danger'>*</span></div>
                    <PaymentElement
                        options={paymentElementOptions}
                        onReady={() => updateStatus('ready')}
                        onLoadError={(e) => { setErrorMsg(e?.error?.message ? getStripeErrorMessage(e.error) : LOAD_ERROR_MESSAGE); updateStatus('error') }}
                        onChange={() => { if (errorMsg) setErrorMsg('') }}
                    />
                </div>
                {errorMsg && <div className='text-danger mt-2' style={{ fontSize: '14px' }}>{errorMsg}</div>}
                {consent}

                <div id='q-box__buttons' className='text-center mt-4 d-flex gap-1 justify-content-center'>
                    {previousButton}
                    <button
                        type="button"
                        name="submit"
                        className={(page == 'reservation' || page == 'addCard') ? 'btn btn-primary d-flex step-btn auto-width' : 'btn btn-primary d-flex step-btn'}
                        style={{ opacity: isLoading ? '0.8' : '1' }}
                        onClick={(event) => {
                            event.stopPropagation()
                            event.preventDefault()
                            handlePay()
                        }}
                        disabled={isLoading || status !== 'ready' || !stripe}
                    >
                        <div className='d-flex justify-content-center align-items-center'>
                            <span className='mb-0'>{page == 'addCard' ? 'Save Card' : page == 'reservation' ? 'Confirm & Book' : 'Submit'}</span>{isLoading ? <span className='ms-2'>
                                <Circles height="20" width="20" style={{ display: 'inline-block' }} color="#ffffff" ariaLabel="circles-loading" visible={true} />
                            </span> : null}
                        </div>
                    </button>
                </div>
            </div>
        </div>
    )
}

StripeCardForm.propTypes = {
    onSubmit: PropTypes.func,
    isSubmittingForm: PropTypes.bool,
    page: PropTypes.string,
    consent: PropTypes.node,
    previousButton: PropTypes.node,
    setValue: PropTypes.func,
    setFormData: PropTypes.func,
    formData: PropTypes.object,
    APILoad: PropTypes.object,
    onInvalid: PropTypes.func
}

// intentMode 'setup': Add Card (no charge; the backend saves the card with a SetupIntent).
// 'payment': the amount is charged; setupFutureUsage 'off_session' only when the card is kept for later
// (the customer's consent, user_consent), so the token matches the PaymentIntent the backend creates.
function PaymentGatewayStripe(props) {
    const { faciltyData, amount, captureMethod, intentMode, setupFutureUsage } = props
    const stripePromise = getStripeForFacility(faciltyData)
    if (!stripePromise) {
        return <div className='text-danger small'>Stripe is not configured for this facility.</div>
    }
    if (intentMode !== 'setup' && !(Number(amount) > 0)) {
        return null
    }
    const options = intentMode === 'setup'
        ? stripeSetupElementsOptions(faciltyData)
        : stripePaymentElementsOptions(faciltyData, amount, captureMethod, setupFutureUsage)
    // key: amount / capture method / mode / card saving are part of the deferred intent options, re-mount if they change
    return (
        <Elements key={`${amount}|${captureMethod}|${intentMode}|${setupFutureUsage || ''}`} stripe={stripePromise} options={options}>
            <StripeCardForm {...props} />
        </Elements>
    )
}

PaymentGatewayStripe.propTypes = {
    faciltyData: PropTypes.object,
    onSubmit: PropTypes.func,
    isSubmittingForm: PropTypes.bool,
    amount: PropTypes.number,
    captureMethod: PropTypes.oneOf([STRIPE_CAPTURE.PRE_AUTH, STRIPE_CAPTURE.SALE]),
    intentMode: PropTypes.oneOf(['payment', 'setup']),
    setupFutureUsage: PropTypes.oneOf(['off_session', null]),
    page: PropTypes.string,
    consent: PropTypes.node,
    previousButton: PropTypes.node,
    setValue: PropTypes.func,
    setFormData: PropTypes.func,
    formData: PropTypes.object,
    APILoad: PropTypes.object,
    onInvalid: PropTypes.func
}

PaymentGatewayStripe.defaultProps = {
    faciltyData: EMPTY_OBJECT,
    onSubmit: noop,
    isSubmittingForm: false,
    amount: 0,
    captureMethod: STRIPE_CAPTURE.SALE,
    intentMode: 'payment',
    setupFutureUsage: null,
    page: '',
    consent: <DefaultComponent />,
    previousButton: <DefaultComponent />,
    setValue: noop,
    setFormData: noop,
    formData: EMPTY_OBJECT,
    APILoad: null
}

export default PaymentGatewayStripe
